import { useEffect, useState } from "react";
import { Highlight } from "prism-react-renderer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import usePrismLanguages from "~/hooks/usePrismLanguages";
import { codeTheme } from "~/lib/codeTheme";
import { cn } from "~/lib/utils";
import { HighlightedCode } from "./HighlightedCode";
import { SubagentActivity } from "./SubagentActivity";
import { parseToolBody } from "./parseToolBody";
import { useSubagentEventsFor } from "./useSubagentEvents";
import type { ToolCallEntry } from "./useTurns";

interface ToolCallCardProps {
  entry: ToolCallEntry;
}

type TabValue = "input" | "progress" | "output";

const TRIGGER_CLS = cn(
  "rounded-none border-b-2 border-transparent px-0 py-1.5 text-xs font-medium text-muted-foreground shadow-none",
  "hover:text-foreground",
  "data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
);

export const ToolCallCard = ({ entry }: ToolCallCardProps) => {
  const pending = entry.result === undefined;
  const subagentEvents = useSubagentEventsFor(entry.id);
  const hasProgress = subagentEvents.length > 0;

  // Default tab choice cascades by what's most useful:
  //   - pending + has progress events → Progress (live activity)
  //   - pending + no progress         → Input
  //   - !pending                      → Output
  // Once the user picks a tab themselves we stop overriding their choice.
  const [tab, setTab] = useState<TabValue>(
    pending ? (hasProgress ? "progress" : "input") : "output"
  );
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    // The Progress tab is conditionally rendered, so its trigger/content
    // can vanish (e.g. events map cleared on a new run while a prior tool
    // call is still pending). If we were sitting on it, fall back to a
    // tab that actually exists — even when the user picked it themselves,
    // since the alternative is a blank panel.
    if (tab === "progress" && !hasProgress) {
      setTab(pending ? "input" : "output");
      return;
    }
    if (touched) return;
    if (!pending) setTab("output");
    else if (hasProgress) setTab("progress");
  }, [pending, hasProgress, touched, tab]);

  const handleValueChange = (next: string) => {
    setTouched(true);
    setTab(next as TabValue);
  };

  return (
    <div className="text-xs focus:outline-none">
      <Tabs value={tab} onValueChange={handleValueChange} className="gap-0">
        <TabsList className="h-auto justify-start gap-4 rounded-none border-b border-border/60 bg-transparent px-3 py-0">
          <TabsTrigger value="input" className={TRIGGER_CLS}>
            Input
          </TabsTrigger>
          {hasProgress && (
            <TabsTrigger value="progress" className={TRIGGER_CLS}>
              Progress
            </TabsTrigger>
          )}
          <TabsTrigger value="output" disabled={pending} className={TRIGGER_CLS}>
            Output
          </TabsTrigger>
        </TabsList>
        <TabsContent value="input">
          <Panel body={entry.args} placeholder="" />
        </TabsContent>
        {hasProgress && (
          <TabsContent value="progress">
            <SubagentActivity events={subagentEvents} />
          </TabsContent>
        )}
        <TabsContent value="output">
          <Panel body={entry.result ?? ""} placeholder="Waiting for result…" />
        </TabsContent>
      </Tabs>
    </div>
  );
};

interface PanelProps {
  body: string;
  placeholder: string;
}

const CODE_CLS =
  "max-h-48 overflow-auto whitespace-pre-wrap break-words px-3 py-2 font-mono text-[11px] leading-relaxed";
// The `code` field is set apart from the plain-text fields with a bordered,
// faintly-filled card, inset from the field header.
const CODE_FIELD_CLS =
  "max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md border border-border/60 bg-muted/40 px-3 py-1.5 font-mono text-[11px] leading-relaxed";
const TEXT_CLS =
  "max-h-48 overflow-auto whitespace-pre-wrap break-words px-3 py-2 font-sans text-xs leading-relaxed";
const SECTION_LABEL_CLS =
  "px-3 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground";

function Panel({ body, placeholder }: PanelProps) {
  const view = parseToolBody(body);

  if (view.kind === "empty") {
    return <pre className={CODE_CLS}>{placeholder}</pre>;
  }
  if (view.kind === "text") {
    return <pre className={TEXT_CLS}>{view.text}</pre>;
  }
  // A plain object gets a field-per-header layout so each key is labelled and
  // its value rendered on its own terms. Arrays and empty objects have no keys
  // worth pulling out, so they stay a single JSON block.
  const { value } = view;
  const entries =
    typeof value === "object" && value !== null && !Array.isArray(value)
      ? Object.entries(value)
      : null;
  if (!entries || entries.length === 0) {
    return <JsonBlock value={value} />;
  }
  return (
    <div className="flex flex-col">
      {entries.map(([key, fieldValue]) => (
        <div key={key} className="flex flex-col">
          <div className={SECTION_LABEL_CLS}>{key}</div>
          <FieldValue name={key} value={fieldValue} />
        </div>
      ))}
    </div>
  );
}

// Object fields whose string value is source code are syntax-highlighted in the
// mapped language (the `code` field is Python, `sql` is SQL); nested
// objects/arrays fall back to a JSON block, everything else is plain text.
const HIGHLIGHTED_FIELD_LANGUAGES: Record<string, string> = {
  code: "python",
  sql: "sql",
};

function FieldValue({ name, value }: { name: string; value: unknown }) {
  const language = HIGHLIGHTED_FIELD_LANGUAGES[name];
  if (language && typeof value === "string") {
    return (
      <HighlightedCode
        code={value}
        language={language}
        className={CODE_FIELD_CLS}
        wrapperClassName="mx-3"
        copyOnHoverOnly
      />
    );
  }
  if (typeof value === "string") {
    return <pre className={TEXT_CLS}>{value}</pre>;
  }
  if (typeof value === "object" && value !== null) {
    return <JsonBlock value={value} />;
  }
  return <pre className={TEXT_CLS}>{String(value)}</pre>;
}

function JsonBlock({ value }: { value: object | unknown[] }) {
  const { Prism } = usePrismLanguages(["json"]);
  // JSON.stringify can throw on circular references — parseToolBody only
  // produces values from JSON.parse / JSON5.parse so we know it's safe, but
  // guard anyway since this is the render boundary.
  let code: string;
  try {
    code = JSON.stringify(value, null, 2);
  } catch {
    code = String(value);
  }
  return (
    <Highlight theme={codeTheme} code={code} language="json" prism={Prism}>
      {({ style, tokens, getLineProps, getTokenProps }) => (
        <pre className={CODE_CLS} style={style}>
          {tokens.map((line, i) => (
            <div key={i} {...getLineProps({ line })}>
              {line.map((token, key) => (
                <span key={key} {...getTokenProps({ token })} />
              ))}
            </div>
          ))}
        </pre>
      )}
    </Highlight>
  );
}
